import { first, size } from "lodash"
import { PERCENT_10000_SCALE, formatValueForDisplay, isNumberFormat, parseValue } from "./formatting"
import { getExcelValue } from "helpers/csv"
import { VALUE_FORMATS } from "helpers/ai"
import { ARTIFACT_KINDS } from "connected-components/dashboard/modules/ai-artifact/types"
import type { Artifact, TableArtifact, PlotArtifact, ArtifactColumn } from "connected-components/dashboard/modules/ai-artifact/types"
import type { DownloadCell, DownloadData } from "connected-components/dashboard/context/download-registry"

/** Minimal shape of a Plotly trace — only the fields accessed during download. */
interface PlotTrace {
  name?: string
  x?: unknown[]
  y?: unknown[]
  labels?: unknown[]
  values?: unknown[]
}

export interface ArtifactDownloadOptions {
  excelCells?: boolean
}

const PERCENT_10000_SIGNIFICANT_DIGITS = 12

const PERCENT_HEADER_SUFFIXES: Partial<Record<string, string>> = {
  [VALUE_FORMATS.PERCENT]: " (%)",
  [VALUE_FORMATS.PERCENT_10000]: " (% squared)",
}

function toCanonicalKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "")
}

/**
 * Resolve a value from a record using accessor candidates.
 * Tries exact key match first, then canonical match (case-insensitive, ignores spaces/symbols).
 */
function getValueFromRecord(record: Record<string, unknown>, accessorCandidates: string[]): unknown {
  const filtered = accessorCandidates.filter(Boolean)

  const directMatch = filtered.find((key) => Object.hasOwn(record, key))
  if (directMatch) return record[directMatch]

  const canonicalCandidates = new Set(filtered.map(toCanonicalKey))
  if (canonicalCandidates.size === 0) return undefined

  return Object.entries(record).find(([recordKey]) => canonicalCandidates.has(toCanonicalKey(recordKey)))?.[1]
}

function getHeaderSuffix(format: string, excelCells: boolean): string {
  if (format === VALUE_FORMATS.PERCENT_100) {
    return excelCells ? "" : " (decimal)"
  }
  return PERCENT_HEADER_SUFFIXES[format] ?? ""
}

function toExcelCell(value: number, options: { currency?: boolean, percent?: boolean }): DownloadCell {
  const cell = getExcelValue(value, options)
  return cell === null ? "" : { ...cell, t: "n" }
}

function toNumericCell(value: unknown, format: string, excelCells: boolean): DownloadCell {
  const parsed: number | null = parseValue(value, format)
  if (parsed === null) return ""

  switch (format) {
    case VALUE_FORMATS.CURRENCY:
      return excelCells ? toExcelCell(parsed, { currency: true }) : parsed
    case VALUE_FORMATS.PERCENT_100:
      return excelCells ? toExcelCell(parsed, { percent: true }) : parsed
    case VALUE_FORMATS.PERCENT_10000:
      return Number((parsed * PERCENT_10000_SCALE).toPrecision(PERCENT_10000_SIGNIFICANT_DIGITS))
    default:
      return parsed
  }
}

/**
 * Converts a table artifact to download format.
 */
function tableArtifactToDownloadData(artifact: TableArtifact, { excelCells = false }: ArtifactDownloadOptions): DownloadData | null {
  const { columns: rawColumns, records } = artifact
  const hasRecords = records?.length > 0
  const hasColumnMetadata = rawColumns?.length > 0
  const firstRecord = first(records)

  if (!hasRecords && !hasColumnMetadata) {
    return null
  }

  const columnAccessors: string[] = hasColumnMetadata
    ? rawColumns.map((col: ArtifactColumn) => col.id || col.field || col.key || col.name || col.label)
    : []

  const labels: string[] = hasColumnMetadata
    ? rawColumns.map((col: ArtifactColumn) => col.label)
    : firstRecord
      ? Object.keys(firstRecord)
      : []

  if (labels.length === 0) {
    return null
  }

  const formats: string[] = labels.map((_label, colIndex) => (hasColumnMetadata && rawColumns[colIndex]?.format) || VALUE_FORMATS.STRING)

  const headers = labels.map((label, colIndex) => `${label}${getHeaderSuffix(formats[colIndex], excelCells)}`)

  const rows = (records || []).map((record) =>
    labels.map((label, colIndex): DownloadCell => {
      const accessorCandidates = hasColumnMetadata
        ? [columnAccessors[colIndex], label]
        : [label]
      const value = getValueFromRecord(record, accessorCandidates)
      const format = formats[colIndex]
      if (isNumberFormat(format)) return toNumericCell(value, format, excelCells)

      const formatted = formatValueForDisplay(value, format)
      return formatted === null || formatted === "" ? "" : String(formatted)
    }),
  )

  return { headers, rows }
}

/**
 * Returns the x-axis values from a set of Plotly traces.
 * Tries `x` arrays first (scatter, bar, line), then `labels` (pie charts).
 */
function getTraceXValues(traces: PlotTrace[]): unknown[] | null {
  const traceWithX = traces.find((t) => Array.isArray(t.x) && t.x.length > 0)
  if (traceWithX?.x) return traceWithX.x

  const traceWithLabels = traces.find((t) => Array.isArray(t.labels) && t.labels.length > 0)
  return traceWithLabels?.labels ?? null
}

/** Returns true when a trace carries its own x-axis data (x array or pie labels). */
function traceHasOwnAxis(trace: PlotTrace): boolean {
  return size(trace.x) > 0 || size(trace.labels) > 0
}

/**
 * Returns the y-axis values from a single Plotly trace.
 * Tries `y` first, then `values` (pie charts).
 */
function getTraceYValues(trace: PlotTrace): unknown[] | null {
  if (Array.isArray(trace.y)) return trace.y
  if (Array.isArray(trace.values)) return trace.values
  return null
}

/**
 * Extracts tabular data from a Plotly figure for download.
 * Handles scatter/bar/line (x/y) and pie charts (labels/values).
 * Multiple traces become additional Y columns.
 */
function plotArtifactToDownloadData(artifact: PlotArtifact): DownloadData | null {
  const traces = artifact.figure?.data as PlotTrace[] | undefined
  if (!Array.isArray(traces) || traces.length === 0) {
    return null
  }

  const xValues = getTraceXValues(traces)
  if (!xValues || xValues.length === 0) {
    return null
  }

  const headers = ["X", ...traces.map((trace, i) => trace.name || `Y${i + 1}`)]

  // No-x traces must not extend the row count: their y-values beyond xValues.length
  // are unanchored and would produce all-empty trailing rows.
  const maxLen = Math.max(
    xValues.length,
    ...traces.filter(traceHasOwnAxis).map((trace) => getTraceYValues(trace)?.length ?? 0),
  )

  const rows: string[][] = []
  for (let i = 0; i < maxLen; i++) {
    rows.push([
      xValues[i] != null ? String(xValues[i]) : "",
      ...traces.map((trace) => {
        if (!traceHasOwnAxis(trace)) return ""

        const yValues = getTraceYValues(trace)
        const val = yValues?.[i]
        return val != null ? String(val) : ""
      }),
    ])
  }

  return { headers, rows }
}

/**
 * Converts an artifact to DownloadData based on its type.
 *
 * @param artifact - TableArtifact or PlotArtifact
 * @param options - `excelCells` wraps currency and percent100 table columns as Excel number cells and drops the " (decimal)" suffix from percent100 headers
 * @returns DownloadData or null if the artifact has no exportable data
 */
export function artifactToDownloadData(artifact: Artifact | null | undefined, options: ArtifactDownloadOptions = {}): DownloadData | null {
  if (!artifact) return null

  if (artifact.type === ARTIFACT_KINDS.TABLE) {
    return tableArtifactToDownloadData(artifact, options)
  }

  if (artifact.type === ARTIFACT_KINDS.PLOT) {
    return plotArtifactToDownloadData(artifact)
  }

  return null
}
